import React, { useState, useEffect } from 'react';
import './RsvpOverlay.css';

// IMPORTANT: Replace this with your actual deployed script URL from "Deploy > New Deployment"
const SHEET_URL = 'https://script.google.com/macros/s/AKfycbwmVV5YeCSwddy26fYxNxJkYsN-Ya6bka73iBmUz1PZuYW3yeU5j89hkZI5KmwPxkYl/exec';

function getCodeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  return code;
}

export default function RsvpOverlay({ open, onClose }) {
  const [attending, setAttending]     = useState(null);
  const [headwear, setHeadwear]       = useState('None');
  const [submitted, setSubmitted]     = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [successName, setSuccessName] = useState('');
  const [codeStatus, setCodeStatus]   = useState('idle');
  const [guestName, setGuestName]     = useState('');

  // Validate code whenever overlay opens
  useEffect(() => {
    if (!open) return;
    const code = getCodeFromUrl();
    if (!code) {
      setCodeStatus('invalid');
      return;
    }

    setCodeStatus('checking');
    
    // We fetch the code status from the script
    fetch(`${SHEET_URL}?code=${encodeURIComponent(code)}`)
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setGuestName(d.guest || '');
          setCodeStatus('valid');
        } else if (d.reason === 'used') {
          setCodeStatus('used');
        } else {
          setCodeStatus('invalid');
        }
      })
      .catch(err => {
        console.error('Validation error:', err);
        // If the validation fails (e.g. CORS), we still let them try to submit
        // The script will catch any invalid/used codes during submission
        setCodeStatus('valid');
      });
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  function reset() {
    setAttending(null);
    setHeadwear('None');
    setSubmitted(false); setSubmitting(false); setError('');
    setCodeStatus('idle'); setGuestName('');
  }
  function handleClose() { reset(); onClose(); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const fd    = new FormData(e.target);
    const name  = fd.get('name')?.trim();
    const email = fd.get('email')?.trim();
    const code  = getCodeFromUrl();

    if (!name)  return setError('Please enter your full name.');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return setError('Please enter a valid email address.');
    if (!attending)
      return setError("Please let us know if you'll be attending.");

    const payload = {
      action: 'submit',
      code,
      name,
      email,
      attendance: attending === 'yes' ? 'Attending 🎉' : 'Not attending',
      headwear:   attending === 'yes' ? headwear : 'N/A',
      allergies:  fd.get('allergies')?.trim() || 'None',
      message:    fd.get('message')?.trim()   || '—',
    };

    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      for (const key in payload) {
        params.append(key, payload[key]);
      }

      // Use 'no-cors' to bypass the CORS block.
      await fetch(SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      setSuccessName(name.split(' ')[0]);
      setSubmitted(true);
      
    } catch (err) {
      console.error('Submission error:', err);
      setError(`Unable to send: ${err.message}. Please check your connection.`);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  // ── Checking ──
  if (codeStatus === 'checking') {
    return (
      <div className="rsvp-overlay">
        <div className="rsvp-overlay__backdrop" onClick={handleClose} />
        <div className="rsvp-overlay__panel">
          <button className="rsvp-overlay__close" onClick={handleClose}>✕</button>
          <div className="rsvp-overlay__inner rsvp-overlay__inner--centre">
            <div className="rsvp-checking">
              <div className="rsvp-checking__spinner" />
              <p>Verifying your invitation…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Already used ──
  if (codeStatus === 'used') {
    return (
      <div className="rsvp-overlay">
        <div className="rsvp-overlay__backdrop" onClick={handleClose} />
        <div className="rsvp-overlay__panel">
          <button className="rsvp-overlay__close" onClick={handleClose}>✕</button>
          <div className="rsvp-overlay__inner rsvp-overlay__inner--centre">
            <div className="rsvp-gate">
              <span className="rsvp-gate__icon">🕊️</span>
              <h3 className="rsvp-gate__title">Already Responded</h3>
              <p className="rsvp-gate__text">
                This invitation has already been used to submit an RSVP.
              </p>
              <p className="rsvp-gate__text" style={{ marginTop: '10px', fontWeight: 'bold' }}>
                If you have any questions, please contact the event organizers.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Invalid / no code ──
  if (codeStatus === 'invalid') {
    return (
      <div className="rsvp-overlay">
        <div className="rsvp-overlay__backdrop" onClick={handleClose} />
        <div className="rsvp-overlay__panel">
          <button className="rsvp-overlay__close" onClick={handleClose}>✕</button>
          <div className="rsvp-overlay__inner rsvp-overlay__inner--centre">
            <div className="rsvp-gate">
              <span className="rsvp-gate__icon">💌</span>
              <h3 className="rsvp-gate__title">Invitation Required</h3>
              <p className="rsvp-gate__text">RSVP is only available via a personal invitation link.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Valid — show form ──
  return (
    <div className="rsvp-overlay">
      <div className="rsvp-overlay__backdrop" onClick={handleClose} />
      <div className="rsvp-overlay__panel">
        <button className="rsvp-overlay__close" onClick={handleClose}>✕</button>
        <div className="rsvp-overlay__inner">

          {submitted ? (
            <div className="rsvp-success">
              <div className="rsvp-success__icon">🤍</div>
              <h2 className="rsvp-success__title">Thank You, {successName}!</h2>
              <p className="rsvp-success__text">
                {attending === 'yes'
                  ? "We're so excited to celebrate with you!"
                  : "We'll miss you, but we appreciate you letting us know."}
              </p>
              <button className="btn-dark" onClick={handleClose} style={{ marginTop: '20px' }}>Close</button>
            </div>
          ) : (
            <>
              <div className="rsvp-overlay__header">
                <h2 className="rsvp-overlay__title">RSVP</h2>
                {guestName && <p className="rsvp-overlay__guest">Welcome, <em>{guestName}</em></p>}
              </div>

              <form className="rsvp-form" onSubmit={handleSubmit} noValidate>
                <div className="rsvp-field">
                  <label>Full Name <span className="rsvp-req">*</span></label>
                  <input name="name" type="text" placeholder="Your full name" required />
                </div>
                <div className="rsvp-field">
                  <label>Your Email <span className="rsvp-req">*</span></label>
                  <input name="email" type="email" placeholder="For your RSVP confirmation" required />
                </div>
                <div className="rsvp-field">
                  <label>Will you be attending? <span className="rsvp-req">*</span></label>
                  <div className="rsvp-pills">
                    <button type="button" className={`rsvp-pill ${attending==='yes' ? 'rsvp-pill--active' : ''}`} onClick={() => setAttending('yes')}>Joyfully Accepts</button>
                    <button type="button" className={`rsvp-pill ${attending==='no' ? 'rsvp-pill--active' : ''}`} onClick={() => setAttending('no')}>Regretfully Declines</button>
                  </div>
                </div>
                {attending === 'yes' && (
                  <div className="rsvp-field">
                    <label>Headwear Preference</label>
                    <div className="rsvp-pills">
                      {['Gele', 'Fila', 'None'].map(v => (
                        <button key={v} type="button" className={`rsvp-pill ${headwear === v ? 'rsvp-pill--active' : ''}`} onClick={() => setHeadwear(v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="rsvp-field">
                  <label>Food Allergies</label>
                  <textarea name="allergies" rows={3} placeholder="Please let us know of any allergies…" />
                </div>
                <div className="rsvp-field">
                  <label>Leave a Message</label>
                  <textarea name="message" rows={3} placeholder="A note for the happy couple…" />
                </div>
                {error && <div className="rsvp-error" style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
                <button type="submit" className="btn-dark" disabled={submitting}>{submitting ? 'Sending…' : 'Send RSVP'}</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}